import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { theme, colorPalette } from "../styles/theme";
import Sidebar from "../components/sidebar/Sidebar";
import InquiryView from "../views/InquiryView";
import UserDetailView from "../views/UserDetailView";
import SampleAdminView from "../sample/SampleAdminView";
import WsSampleAdminView from "../ws_sample/WsSampleAdminView";
import { wsApi } from "../api/services";
import { isSampleAccount, isMasterAccount, isOrgLevelAdmin, isRiskInfoRestrictedAdmin } from "../utils/auth";

/**
 * 대시보드 메인 페이지
 * - 전체 레이아웃(사이드바 + 메인 영역)을 구성합니다.
 * - 현재 선택된 뷰(currentView) 상태를 관리하고 해당 뷰를 렌더링합니다.
 * - 데이터 페칭은 useWsData 훅에 위임합니다.
 *
 * @param {object} me - 로그인한 관리자 정보
 * @param {Function} logout - 로그아웃 핸들러
 * @param {Function} onChangePassword - 비밀번호 변경 핸들러
 */
const Dashboard = ({ me, logout, onChangePassword }) => {
  // 현재 표시할 뷰 ('sample' | 'inquiry')
  const [currentView, setCurrentView] = useState("sample");

  // 사이드바(소속/사업 목록) - 마스터 관리자(level === 0)만 조회
  const [organizations, setOrganizations] = useState([]);

  // 현재 선택된 프로젝트(사업) - 마스터 관리자는 사이드바에서 직접 선택,
  // 단일 사업 계정(소속 관리자 등)은 본인 code가 곧 프로젝트 code
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectUsers, setProjectUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(null);

  // 검사 통계 - 참가자 목록과 분리된 별도 API(GET /assessments/program-stats)로 조회
  const [projectStats, setProjectStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(null);

  // 자살/자해 위험 감지 상세 - user_id 기준 맵 ({ user_id: { detected_at, last_detected_at, ... } })
  const [riskDetailsByUserId, setRiskDetailsByUserId] = useState({});

  const isMasterAdmin = isMasterAccount(me);
  const isSample = isSampleAccount(me);
  // code 없이 소속(기관) 산하 여러 사업을 한 번에 봐야 하는 기관 관리자 — 서버가 토큰 기준으로 이미 범위를 걸러 내려줌
  const isOrgAdmin = isOrgLevelAdmin(me);
  // 월성복지관 소속 level 5 관리자(마스터 제외) - 위험(자살·자해 위험 감지) 관련 정보 비노출
  const hideRiskInfo = isRiskInfoRestrictedAdmin(me);

  useEffect(() => {
    if (!isMasterAdmin && !isOrgAdmin) return;

    wsApi
      .getSideBar()
      .then((res) => {
        setOrganizations(res.data?.organizations ?? []);
      })
      .catch((err) => {
        // console.error("[사이드바 API 오류]", err);
      });
  }, [isMasterAdmin, isOrgAdmin]);

  useEffect(() => {
    if (me?.code && !isMasterAdmin && !isSample) {
      setSelectedProject({
        code: me.code,
        name: me.organization || "소속 관리",
        organizationName: me.organization,
      });
    }
  }, [me?.code, me?.organization, isMasterAdmin, isSample]);

  const handleProjectSelect = (project) => {
    setSelectedProject(project);
    setCurrentView("sample");
  };

  useEffect(() => {
    if (!selectedProject?.code) return;

    setUsersLoading(true);
    setUsersError(null);

    wsApi
      .getUsersByCode(selectedProject.code)
      .then((res) => {
        setProjectUsers(res.data?.users ?? []);
      })
      .catch((err) => {
        // console.error("[참가자 목록 API 오류]", err);
        setUsersError("참가자 목록을 불러오는 중 오류가 발생했습니다.");
        setProjectUsers([]);
      })
      .finally(() => {
        setUsersLoading(false);
      });
  }, [selectedProject?.code]);

  // 검사 통계 - date 생략 시 서버가 주별 통계 4주치를 응답
  useEffect(() => {
    if (!selectedProject?.code) return;

    setStatsLoading(true);
    setStatsError(null);

    wsApi
      .getProgramStats(selectedProject.code)
      .then((res) => {
        setProjectStats(res.data ?? null);
      })
      .catch((err) => {
        // console.error("[검사 통계 API 오류]", err);
        setStatsError("검사 통계를 불러오는 중 오류가 발생했습니다.");
        setProjectStats(null);
      })
      .finally(() => {
        setStatsLoading(false);
      });
  }, [selectedProject?.code]);

  // "위험" 배지가 붙은 참가자(has_suicide_risk)가 있을 때만 자살/자해 위험 감지 상세를 조회
  useEffect(() => {
    if (!selectedProject?.code || !projectUsers.some((u) => u.has_suicide_risk)) {
      setRiskDetailsByUserId({});
      return;
    }

    wsApi
      .getSuicideRiskUsers(selectedProject.code)
      .then((res) => {
        const items = res.data?.items ?? [];
        const map = {};
        items.forEach((item) => {
          map[item.user_id] = item;
        });
        setRiskDetailsByUserId(map);
      })
      .catch((err) => {
        // console.error("[자살 위험 감지 API 오류]", err);
        setRiskDetailsByUserId({});
      });
  }, [selectedProject?.code, projectUsers]);

  // 현재 CODE에 따른 상단 타이틀 결정
  const getTitle = () => {
    if (isMasterAdmin) return "슈퍼 관리자";
    if (isSample) return "샘플 테스트 계정";
    if (me?.code === "WS-YOUTH-2026") return "2026 청년도전지원사업";
    if (me?.level === 9 && me?.code?.startsWith("ADMIN-TEST")) return "샘플 테스트 계정";
    if (isOrgAdmin) return me?.organization ? `${me.organization}` : "기관 관리자";
    return "관리 시스템";
  };

  // 샘플 계정은 공용 사이드바/타이틀 바 대신 샘플 전용 전체 레이아웃을 사용
  // 주소에 ?ws 가 붙어 있으면 월성복지관 실데이터(CSV) 기반 WS 샘플 화면을 보여 줌
  if (isSample) {
    const isWsSample = new URLSearchParams(window.location.search).has("ws");
    const SampleView = isWsSample ? WsSampleAdminView : SampleAdminView;
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SampleView me={me} onChangePassword={onChangePassword} onLogout={logout} />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          display: "flex",
          height: "100vh",
          p: 2,
          gap: 2,
          backgroundColor: colorPalette.background,
        }}
      >
        {/* 1. 좌측 사이드바 */}
        <Sidebar
          me={me}
          onChangePassword={onChangePassword}
          onLogout={logout}
          currentView={currentView}
          onViewChange={setCurrentView}
          organizations={organizations}
          selectedProject={selectedProject}
          onProjectSelect={handleProjectSelect}
        />

        {/* 2. 메인 컨텐츠 영역 */}
        <Box
          sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2, overflow: "hidden" }}
        >
          {/* 상단 타이틀 바 */}
          <Paper sx={{ p: 1.5, pl: 3, flexShrink: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 500, fontSize: 16 }}>
              {getTitle()}
            </Typography>
          </Paper>

          {/* 뷰 영역: currentView에 따라 동적으로 렌더링 */}
          {currentView === "sample" && (
            <UserDetailView
              project={selectedProject}
              participants={projectUsers}
              stats={projectStats}
              loading={usersLoading}
              error={usersError}
              statsLoading={statsLoading}
              statsError={statsError}
              isMasterAdmin={isMasterAdmin}
              riskDetailsByUserId={riskDetailsByUserId}
              hideRiskInfo={hideRiskInfo}
            />
          )}
          {currentView === "inquiry" && <InquiryView />}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default Dashboard;
